import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/custom/Navbar";
import WeatherWidget from "@/components/custom/WeatherWidget";
import ChatPanel from "@/components/custom/ChatPanel";
import MunicipalitySelector from "@/components/custom/MunicipalitySelector";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, ShieldAlert, Package, Users, Truck, Home, Stethoscope,
  HelpCircle, MapPin, Clock, Eye, CheckCircle, XCircle, Building2,
  Edit3, AlertTriangle, Navigation, MessageCircle, Siren, Share2,
  CloudRain, Flame, Wind, Zap, Snowflake, PawPrint, Bug, ClipboardList,
} from "lucide-react";
import { ShareEmergencyButton } from "@/components/custom/ShareButtons";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `2px solid ${C.navy}`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

function BCard({ children, className = "", style = {} }) {
  return (
    <div className={`bg-white ${className}`} style={{ borderRadius: 4, ...style }}>
      {children}
    </div>
  );
}

function BBadge({ children, bg = C.navy, color = "#fff" }) {
  return (
    <span className="inline-block px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ fontFamily: B.mono, background: bg, color, borderRadius: 3 }}
    >{children}</span>
  );
}

function SectionTitle({ icon: Icon, iconColor, children, color = C.navy, kicker }) {
  return (
    <div className="mb-8">
      {kicker && (
        <p className="text-[11px] font-bold tracking-[0.25em] uppercase mb-2 flex items-center gap-2" style={{ fontFamily: MONO, color: iconColor || C.blue }}>
          <Icon className="w-4 h-4" style={{ color: iconColor || C.blue }} />
          {kicker}
        </p>
      )}
      <h3 className="text-[clamp(1.8rem,3.5vw,2.6rem)] leading-[0.95] tracking-tight" style={{ color, fontFamily: DISPLAY }}>
        {children}
      </h3>
    </div>
  );
}

function FullBleed({ bg, children, testId }) {
  return (
    <section style={{ background: bg, marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }} data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">{children}</div>
    </section>
  );
}

function BBtn({ children, onClick, disabled, variant = "primary", className = "", testId, style = {} }) {
  const styles = {
    primary: { background: C.navy, color: "#fff", border: "none" },
    secondary: { background: C.blue, color: "#fff", border: "none" },
    danger: { background: C.red, color: "#fff", border: "none" },
    dangerPulse: { background: "#dc2626", color: "#fff", border: "none" },
    ghost: { background: "transparent", color: C.navy, border: `2px solid ${C.navy}30` },
    outline: { background: "#fff", color: C.navy, border: `2px solid ${C.navy}` },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center gap-1.5 ${className}`}
      style={{ fontFamily: B.mono, borderRadius: 4, ...styles[variant], ...style }}
    >
      {children}
    </button>
  );
}

const EMERGENCY_TYPES = [
  { value: "dana", label: "DANA", Icon: CloudRain, description: "Depresión Aislada en Niveles Altos" },
  { value: "inundacion", label: "Inundación", Icon: CloudRain, description: "Desbordamientos, riadas" },
  { value: "incendio", label: "Incendio", Icon: Flame, description: "Fuego forestal o urbano" },
  { value: "terremoto", label: "Terremoto", Icon: AlertTriangle, description: "Movimiento sísmico" },
  { value: "huracan", label: "Huracán/Tormenta", Icon: Wind, description: "Vientos extremos, ciclones" },
  { value: "sequia", label: "Sequía", Icon: Zap, description: "Falta de agua y recursos" },
  { value: "nevada", label: "Nevada/Ola de frío", Icon: Snowflake, description: "Temporal de nieve, frío extremo" },
  { value: "ataque", label: "Ataque/Intrusión", Icon: ShieldAlert, description: "Robo, vandalismo, agresión" },
  { value: "abandono_masivo", label: "Abandono masivo", Icon: PawPrint, description: "Llegada masiva de animales" },
  { value: "epidemia", label: "Epidemia/Brote", Icon: Bug, description: "Enfermedad contagiosa" },
  { value: "otro", label: "Otro", Icon: ClipboardList, description: "Otra emergencia" },
];

const NEED_TYPES = [
  { value: "material", label: "Material", icon: Package },
  { value: "voluntarios", label: "Voluntarios", icon: Users },
  { value: "transporte", label: "Transporte", icon: Truck },
  { value: "acogida", label: "Acogida", icon: Home },
  { value: "veterinario", label: "Veterinario", icon: Stethoscope },
  { value: "otro", label: "Otro", icon: HelpCircle },
];

const URGENCY_LEVELS = [
  { value: "urgente", label: "URGENTE", color: C.red },
  { value: "prioridad", label: "PRIORIDAD", color: "#fa9000" },
  { value: "activo", label: "ACTIVO", color: C.blue },
  { value: "info", label: "INFO", color: C.lilac },
];

const dialogStyle = { borderRadius: 4 };
const inputStyle = { border: `2px solid ${C.navy}`, borderRadius: 2, background: "#fff" };
const selectStyle = { border: `2px solid ${C.navy}`, borderRadius: 2, background: "#fff" };

export default function ShelterDashboard() {
  const { user, setUser } = useAuth();
  const [emergencies, setEmergencies] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [offers, setOffers] = useState({});
  const [showNewEmergency, setShowNewEmergency] = useState(false);
  const [showNewNeed, setShowNewNeed] = useState(false);
  const [showOffers, setShowOffers] = useState(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [activeTab, setActiveTab] = useState("emergencies");
  const [emergencyForm, setEmergencyForm] = useState({
    type: "dana", title: "", description: "", location_name: "", location_lat: null, location_lng: null,
  });
  const [locating, setLocating] = useState(false);
  const [needForm, setNeedForm] = useState({
    emergency_id: "", type: "material", title: "", description: "", quantity: "", urgency: "activo",
  });
  const [editingNeed, setEditingNeed] = useState(null);
  const [editNeedForm, setEditNeedForm] = useState({
    title: "", description: "", quantity: "", urgency: "activo",
  });
  const [editingEmergency, setEditingEmergency] = useState(null);
  const [editEmergencyForm, setEditEmergencyForm] = useState({
    title: "", description: "", type: "dana", location_name: "",
  });
  const [profileForm, setProfileForm] = useState({
    name: "", bio: "", organization_name: "", location_name: "", location_lat: null, location_lng: null,
  });
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("ea_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(() => {
    if (!user) return;
    axios
      .get(`${API}/shelters/${user.user_id}/emergencies`, { headers, withCredentials: true })
      .then((r) => setEmergencies(r.data))
      .catch(() => {});
    axios
      .get(`${API}/needs?status=abierta`)
      .then((r) => {
        const myNeeds = r.data.filter((n) => n.shelter_id === user.user_id);
        setNeeds(myNeeds);
      })
      .catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || "", bio: user.bio || "", organization_name: user.organization_name || "",
        location_name: user.location_name || "", location_lat: user.location_lat || null, location_lng: user.location_lng || null,
      });
    }
  }, [user]);

  const handleCreateEmergency = async () => {
    if (!emergencyForm.title.trim()) { toast.error("El título es obligatorio"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/emergencies`, emergencyForm, { headers, withCredentials: true });
      toast.success("Emergencia creada");
      setShowNewEmergency(false);
      setEmergencyForm({ type: "dana", title: "", description: "", location_name: "", location_lat: null, location_lng: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear emergencia");
    } finally { setLoading(false); }
  };

  const handleCreateNeed = async () => {
    if (!needForm.title.trim() || !needForm.emergency_id) {
      toast.error("Selecciona una emergencia y escribe un título");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/needs`, needForm, { headers, withCredentials: true });
      toast.success("Necesidad publicada");
      setShowNewNeed(false);
      setNeedForm({ emergency_id: "", type: "material", title: "", description: "", quantity: "", urgency: "activo" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear necesidad");
    } finally { setLoading(false); }
  };

  const handleEditEmergency = (emg) => {
    setEditingEmergency(emg);
    setEditEmergencyForm({
      title: emg.title || "", description: emg.description || "",
      type: emg.type || "dana", location_name: emg.location_name || "",
    });
  };

  const handleSaveEmergencyEdit = async () => {
    if (!editingEmergency) return;
    try {
      await axios.put(`${API}/emergencies/${editingEmergency.emergency_id}`, editEmergencyForm, { headers, withCredentials: true });
      toast.success("Emergencia actualizada");
      setEditingEmergency(null);
      fetchData();
    } catch { toast.error("Error al actualizar emergencia"); }
  };

  const fetchOffers = async (needId) => {
    try {
      const res = await axios.get(`${API}/offers/need/${needId}`, { headers, withCredentials: true });
      setOffers((prev) => ({ ...prev, [needId]: res.data }));
      setShowOffers(needId);
    } catch { toast.error("Error al cargar ofertas"); }
  };

  const handleOfferStatus = async (offerId, status) => {
    try {
      await axios.put(`${API}/offers/${offerId}/status`, { status }, { headers, withCredentials: true });
      toast.success(`Oferta ${status}`);
      if (showOffers) fetchOffers(showOffers);
    } catch { toast.error("Error al actualizar oferta"); }
  };

  const handleEditNeed = (need) => {
    setEditingNeed(need);
    setEditNeedForm({ title: need.title, description: need.description, quantity: need.quantity || "", urgency: need.urgency });
  };

  const handleSaveNeedEdit = async () => {
    if (!editingNeed) return;
    try {
      await axios.put(`${API}/needs/${editingNeed.need_id}`, editNeedForm, { headers, withCredentials: true });
      toast.success("Necesidad actualizada");
      setEditingNeed(null);
      fetchData();
    } catch { toast.error("Error al actualizar necesidad"); }
  };

  const handleCloseEmergency = async (emergencyId) => {
    try {
      await axios.put(`${API}/emergencies/${emergencyId}`, { status: "cerrada" }, { headers, withCredentials: true });
      toast.success("Emergencia cerrada");
      fetchData();
    } catch { toast.error("Error al cerrar emergencia"); }
  };

  const [activatingSOS, setActivatingSOS] = useState(null);

  const handleActivateSOS = async (emergencyId) => {
    if (!window.confirm("¿Activar SOS de EMERGENCIA? Se alertará a TODOS los voluntarios cercanos (50km). Usa esta función solo en situaciones críticas.")) return;
    setActivatingSOS(emergencyId);
    try {
      const res = await axios.post(`${API}/emergencies/${emergencyId}/sos`, {}, { headers });
      toast.success(res.data.message, { duration: 5000 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al activar SOS");
    } finally { setActivatingSOS(null); }
  };

  const handleDeactivateSOS = async (emergencyId) => {
    try {
      await axios.put(`${API}/emergencies/${emergencyId}/sos/deactivate`, {}, { headers });
      toast.success("SOS desactivado");
      fetchData();
    } catch { toast.error("Error al desactivar SOS"); }
  };

  const handleCloseNeed = async (needId) => {
    try {
      await axios.put(`${API}/needs/${needId}`, { status: "cerrada" }, { headers, withCredentials: true });
      toast.success("Necesidad cerrada");
      fetchData();
    } catch { toast.error("Error al cerrar necesidad"); }
  };

  const handleSaveProfile = async () => {
    try {
      const res = await axios.put(`${API}/auth/profile`, profileForm, { headers, withCredentials: true });
      setUser(res.data);
      localStorage.setItem("ea_user", JSON.stringify(res.data));
      setShowEditProfile(false);
      toast.success("Perfil actualizado");
    } catch { toast.error("Error al guardar perfil"); }
  };

  if (!user) return null;

  const activeEmergencies = emergencies.filter((e) => e.status === "activo");

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="shelter-dashboard" style={{ background: C.offwhite, fontFamily: B.font }}>
      <Navbar />

      <div className="pt-28 pb-16 max-w-7xl mx-auto px-6 md:px-8">
        {/* ── Profile banner ── */}
        <div className="mb-10 pb-8" style={{ borderBottom: `2px solid ${C.navy}` }}>
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 flex items-center justify-center" style={{ background: C.navy, borderRadius: 4 }}>
                <Building2 className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl tracking-tight" style={{ color: C.navy, fontFamily: DISPLAY }}>
                  {user.organization_name || user.name}
                </h1>
                <p className="text-xs tracking-widest mt-1.5" style={{ fontFamily: B.mono, color: C.blue }}>{user.email}</p>
                {user.location_name && (
                  <p className="text-xs tracking-widest flex items-center gap-1.5 mt-1" style={{ fontFamily: B.mono, color: C.gray }}>
                    <MapPin className="w-3.5 h-3.5" /> {user.location_name}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowEditProfile(true)}
              className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all hover:-translate-y-0.5"
              style={{ fontFamily: B.mono, color: C.navy, border: `2px solid ${C.navy}`, borderRadius: 4, background: "#fff" }}
              data-testid="edit-shelter-profile-btn"
            >
              <Edit3 className="w-4 h-4" /> Editar
            </button>
          </div>
          {/* Inline stats + badge */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            <span className="text-xs font-bold tracking-widest uppercase" style={{ fontFamily: B.mono, background: C.blue, color: "#fff", padding: "4px 14px", borderRadius: 3 }}>Protectora</span>
            <span className="flex items-center gap-2" data-testid="stat-emergencies">
              <ShieldAlert className="w-5 h-5" style={{ color: C.red }} />
              <span className="text-2xl font-bold" style={{ color: C.navy, fontFamily: DISPLAY }}>{activeEmergencies.length}</span>
              <span className="text-xs tracking-wider" style={{ fontFamily: B.mono, color: C.gray }}>emergencias activas</span>
            </span>
            <span className="flex items-center gap-2" data-testid="stat-needs">
              <Package className="w-5 h-5" style={{ color: C.blue }} />
              <span className="text-2xl font-bold" style={{ color: C.navy, fontFamily: DISPLAY }}>{needs.length}</span>
              <span className="text-xs tracking-wider" style={{ fontFamily: B.mono, color: C.gray }}>necesidades</span>
            </span>
          </div>
        </div>

        {/* ── Weather (amplio, protagonista) ── */}
        <div className="mb-10">
          <WeatherWidget userLat={user?.location_lat} userLng={user?.location_lng} userName={user?.location_name} />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-10">
          <BBtn onClick={() => setShowNewEmergency(true)} variant="primary" testId="create-emergency-btn" style={{ padding: "12px 20px", fontSize: "0.85rem" }}>
            <AlertTriangle className="w-5 h-5" />
            NUEVA EMERGENCIA
          </BBtn>
          <BBtn
            onClick={() => setShowNewNeed(true)}
            disabled={activeEmergencies.length === 0}
            variant="secondary"
            testId="create-need-btn"
            style={{ padding: "12px 20px", fontSize: "0.85rem" }}
          >
            <Plus className="w-5 h-5" />
            PUBLICAR NECESIDAD
          </BBtn>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mb-0" style={{ borderBottom: `2px solid ${C.navy}20` }}>
          {[
            { id: "emergencies", label: "Emergencias", testId: "tab-emergencies" },
            { id: "needs", label: "Necesidades", testId: "tab-needs" },
            { id: "chat", label: "Chat", icon: MessageCircle, testId: "tab-chat" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={tab.testId}
              className="px-6 py-3 text-sm font-bold uppercase tracking-[0.12em] flex items-center gap-2 transition-colors relative"
              style={{
                fontFamily: B.mono,
                background: "transparent",
                color: activeTab === tab.id ? C.navy : C.lightgray,
                borderBottom: activeTab === tab.id ? `3px solid ${C.navy}` : "3px solid transparent",
                marginBottom: "-2px",
              }}
            >
              {tab.icon && <tab.icon className="w-4 h-4" />}
              {tab.label}
            </button>
          ))}
        </div>

        {/* EMERGENCIES TAB — banda ancha ámbar */}
        {activeTab === "emergencies" && (
          <FullBleed bg={C.orange} testId="shelter-emergencies-block">
            <SectionTitle icon={ShieldAlert} iconColor={C.navy} color={C.navy} kicker="Coordina la respuesta">Tus emergencias</SectionTitle>
            {emergencies.length > 0 ? (
              <div className="space-y-4">
                {emergencies.map((emg) => (
                  <div key={emg.emergency_id} className="p-5 md:p-6" style={{ background: "#fff", border: `2px solid ${C.navy}`, borderRadius: 4 }} data-testid={`shelter-emergency-${emg.emergency_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <BBadge bg={emg.status === "activo" ? C.red : "#ccc"} color="#fff">
                            {emg.type}
                          </BBadge>
                          <BBadge bg={emg.status === "activo" ? C.olive : "#999"} color="#fff">
                            {emg.status}
                          </BBadge>
                          {emg.sos_active && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest animate-pulse"
                              style={{ fontFamily: B.mono, background: "#dc2626", color: "#fff", borderRadius: 4 }}>
                              <Siren className="w-3 h-3" /> SOS ACTIVO
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg md:text-xl font-bold" style={{ color: C.navy }}>{emg.title}</h3>
                        <p className="text-base mt-1.5 leading-relaxed" style={{ color: C.gray }}>{emg.description}</p>
                        <div className="flex items-center gap-4 mt-3 flex-wrap">
                          <span className="flex items-center gap-1.5 text-xs tracking-wider" style={{ fontFamily: B.mono, color: C.gray }}>
                            <MapPin className="w-3.5 h-3.5" /> {emg.location_name || "Sin ubicación"}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs tracking-wider" style={{ fontFamily: B.mono, color: C.gray }}>
                            <Clock className="w-3.5 h-3.5" /> {new Date(emg.created_at).toLocaleDateString("es-ES")}
                          </span>
                          <span className="text-xs tracking-wider font-bold" style={{ fontFamily: B.mono, color: C.blue }}>
                            {emg.needs_count || 0} necesidades
                          </span>
                          <ShareEmergencyButton emergency={emg} className="ml-auto" />
                        </div>
                      </div>
                      {emg.status === "activo" && (
                        <div className="flex flex-col items-end gap-2 ml-4">
                          {emg.sos_active ? (
                            <BBtn onClick={() => handleDeactivateSOS(emg.emergency_id)} variant="dangerPulse" testId={`deactivate-sos-${emg.emergency_id}`} className="animate-pulse">
                              <Siren className="w-3.5 h-3.5" /> DESACTIVAR SOS
                            </BBtn>
                          ) : (
                            <BBtn onClick={() => handleActivateSOS(emg.emergency_id)} disabled={activatingSOS === emg.emergency_id} variant="danger" testId={`activate-sos-${emg.emergency_id}`}>
                              <Siren className="w-3.5 h-3.5" /> {activatingSOS === emg.emergency_id ? "ACTIVANDO..." : "SOS EMERGENCIA"}
                            </BBtn>
                          )}
                          <BBtn onClick={() => handleEditEmergency(emg)} variant="outline" testId={`edit-emergency-${emg.emergency_id}`}>
                            <Edit3 className="w-3.5 h-3.5" /> EDITAR
                          </BBtn>
                          <BBtn onClick={() => handleCloseEmergency(emg.emergency_id)} variant="ghost" testId={`close-emergency-${emg.emergency_id}`}>
                            CERRAR
                          </BBtn>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-10 text-center" style={{ background: "rgba(255,255,255,0.55)", border: `2px solid ${C.navy}`, borderRadius: 4 }}>
                <ShieldAlert className="w-10 h-10 mx-auto mb-3" style={{ color: C.navy }} />
                <p className="text-base md:text-lg" style={{ color: C.navy }}>
                  No tienes emergencias registradas. Si llega el momento, aquí podrás pedir ayuda de forma ordenada.
                </p>
              </div>
            )}
          </FullBleed>
        )}

        {/* NEEDS TAB — banda ancha azul noche */}
        {activeTab === "needs" && (
          <FullBleed bg={C.navy} testId="shelter-needs-block">
            <SectionTitle icon={Package} iconColor={C.orange} color={C.white} kicker="Lo que tu entidad necesita">Tus necesidades</SectionTitle>
            {needs.length > 0 ? (
              <div className="space-y-4">
                {needs.map((need) => {
                  const urgencyColor = URGENCY_LEVELS.find((u) => u.value === need.urgency)?.color || C.blue;
                  return (
                    <div key={need.need_id} className="p-5 md:p-6" style={{ background: "#fff", border: `2px solid ${C.navy}`, borderLeft: `8px solid ${urgencyColor}`, borderRadius: 4 }} data-testid={`shelter-need-${need.need_id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <BBadge bg={urgencyColor}>{need.urgency}</BBadge>
                            <BBadge bg={C.navy}>{need.type}</BBadge>
                          </div>
                          <h3 className="text-lg md:text-xl font-bold" style={{ color: C.navy }}>{need.title}</h3>
                          <p className="text-base mt-1.5 leading-relaxed" style={{ color: C.gray }}>{need.description}</p>
                          {need.quantity && (
                            <p className="text-xs mt-2 tracking-wider font-bold" style={{ fontFamily: B.mono, color: C.blue }}>
                              CANTIDAD: {need.quantity}
                            </p>
                          )}
                          <p className="text-xs mt-2 tracking-wider" style={{ fontFamily: B.mono, color: C.gray }}>
                            {need.offers_count || 0} ofertas recibidas
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <BBtn onClick={() => handleEditNeed(need)} variant="ghost" testId={`edit-need-${need.need_id}`}>
                            <Edit3 className="w-3 h-3" /> EDITAR
                          </BBtn>
                          <BBtn onClick={() => fetchOffers(need.need_id)} variant="ghost" testId={`view-offers-${need.need_id}`}>
                            <Eye className="w-3 h-3" /> OFERTAS
                          </BBtn>
                          <BBtn onClick={() => handleCloseNeed(need.need_id)} variant="ghost" testId={`close-need-${need.need_id}`}>
                            CERRAR
                          </BBtn>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-10 text-center" style={{ borderTop: "2px solid rgba(255,255,255,0.2)" }}>
                <Package className="w-10 h-10 mx-auto mb-3" style={{ color: "rgba(255,255,255,0.5)" }} />
                <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.75)" }}>
                  No tienes necesidades publicadas. Cuando publiques una, la red de voluntariado la verá al momento.
                </p>
              </div>
            )}
          </FullBleed>
        )}

        {/* CHAT TAB */}
        {activeTab === "chat" && <ChatPanel />}
      </div>

      {/* ============ DIALOGS ============ */}

      {/* New Emergency Dialog */}
      <Dialog open={showNewEmergency} onOpenChange={setShowNewEmergency}>
        <DialogContent className="sm:max-w-lg" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: C.red, borderRadius: 4 }}>
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
              NUEVA EMERGENCIA
            </DialogTitle>
            <DialogDescription style={{ fontFamily: B.mono, fontSize: "11px" }}>
              Registra una situación de emergencia para solicitar ayuda
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TIPO DE EMERGENCIA</Label>
              <Select value={emergencyForm.type} onValueChange={(v) => setEmergencyForm({ ...emergencyForm, type: v })}>
                <SelectTrigger className="mt-1" style={selectStyle} data-testid="emergency-type-select">
                  <SelectValue placeholder="Selecciona el tipo..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] z-[100]" position="popper" sideOffset={4} style={{ borderRadius: 4,  }}>
                  {EMERGENCY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <t.Icon className="w-4 h-4" style={{ color: C.navy }} strokeWidth={1.8} />
                        <div>
                          <span className="font-medium">{t.label}</span>
                          <span className="text-xs text-gray-400 ml-2">{t.description}</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TÍTULO</Label>
              <Input
                value={emergencyForm.title}
                onChange={(e) => setEmergencyForm({ ...emergencyForm, title: e.target.value })}
                placeholder="Ej: DANA en Valencia - Refugio El Abrazo"
                className="mt-1"
                style={inputStyle}
                data-testid="emergency-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESCRIPCIÓN</Label>
              <Textarea
                value={emergencyForm.description}
                onChange={(e) => setEmergencyForm({ ...emergencyForm, description: e.target.value })}
                placeholder="Describe la situación..."
                className="mt-1"
                style={inputStyle}
                data-testid="emergency-description-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>UBICACIÓN</Label>
              <div className="flex gap-2 mt-1">
                <MunicipalitySelector
                  value={emergencyForm.location_name}
                  onChange={(name) => setEmergencyForm({ ...emergencyForm, location_name: name })}
                  onLocationSelect={(lat, lng, name) => {
                    setEmergencyForm((f) => ({ ...f, location_lat: lat, location_lng: lng, location_name: name }));
                    toast.success(`${name} seleccionado`);
                  }}
                  placeholder="Buscar municipio..."
                  className="flex-1"
                />
                <button
                  type="button"
                  disabled={locating}
                  onClick={() => {
                    if (!navigator.geolocation) return;
                    setLocating(true);
                    navigator.geolocation.getCurrentPosition(
                      async (pos) => {
                        const lat = pos.coords.latitude;
                        const lng = pos.coords.longitude;
                        let name = "";
                        try {
                          const geo = await axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=es`);
                          const addr = geo.data?.address;
                          name = addr?.city || addr?.town || addr?.village || addr?.state || "";
                        } catch {}
                        setEmergencyForm((f) => ({ ...f, location_lat: lat, location_lng: lng, location_name: name || f.location_name }));
                        setLocating(false);
                        toast.success(name ? `${name}` : "Coordenadas guardadas");
                      },
                      () => { setLocating(false); toast.error("No se pudo obtener ubicación"); },
                      { enableHighAccuracy: true, timeout: 10000 }
                    );
                  }}
                  className="w-10 h-10 flex items-center justify-center hover:bg-[C.blue10] transition-colors"
                  style={{ border: "1px solid C.blue", borderRadius: 4 }}
                  title="Usar mi ubicación actual"
                  data-testid="emergency-geolocate-btn"
                >
                  <Navigation className={`w-4 h-4 ${locating ? "animate-spin" : ""}`} style={{ color: C.blue }} />
                </button>
              </div>
              {emergencyForm.location_lat && (
                <p className="text-[10px] mt-1 flex items-center gap-1" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>
                  <MapPin className="w-3 h-3" />
                  {emergencyForm.location_lat.toFixed(4)}, {emergencyForm.location_lng.toFixed(4)}
                </p>
              )}
            </div>
            <button
              onClick={handleCreateEmergency}
              disabled={loading}
              className="w-full py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-50"
              style={{ fontFamily: B.font, background: C.red, color: "#fff", borderRadius: 4 }}
              data-testid="submit-emergency-btn"
            >
              {loading ? "CREANDO..." : "REGISTRAR EMERGENCIA"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Need Dialog */}
      <Dialog open={showNewNeed} onOpenChange={setShowNewNeed}>
        <DialogContent className="sm:max-w-lg" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: C.blue, borderRadius: 4 }}>
                <Package className="w-4 h-4 text-white" />
              </div>
              PUBLICAR NECESIDAD
            </DialogTitle>
            <DialogDescription style={{ fontFamily: B.mono, fontSize: "11px" }}>
              Indica qué necesitas para que los voluntarios puedan ayudar
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>EMERGENCIA ASOCIADA</Label>
              <Select value={needForm.emergency_id} onValueChange={(v) => setNeedForm({ ...needForm, emergency_id: v })}>
                <SelectTrigger className="mt-1" style={selectStyle} data-testid="need-emergency-select">
                  <SelectValue placeholder="Selecciona..." />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: 4,  }}>
                  {activeEmergencies.map((e) => (
                    <SelectItem key={e.emergency_id} value={e.emergency_id}>{e.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TIPO DE NECESIDAD</Label>
              <Select value={needForm.type} onValueChange={(v) => setNeedForm({ ...needForm, type: v })}>
                <SelectTrigger className="mt-1" style={selectStyle} data-testid="need-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: 4,  }}>
                  {NEED_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TÍTULO</Label>
              <Input
                value={needForm.title}
                onChange={(e) => setNeedForm({ ...needForm, title: e.target.value })}
                placeholder="Ej: Necesitamos 50 mantas"
                className="mt-1"
                style={inputStyle}
                data-testid="need-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESCRIPCIÓN</Label>
              <Textarea
                value={needForm.description}
                onChange={(e) => setNeedForm({ ...needForm, description: e.target.value })}
                placeholder="Detalla la necesidad..."
                className="mt-1"
                style={inputStyle}
                data-testid="need-description-input"
              />
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CANTIDAD</Label>
                <Input
                  value={needForm.quantity}
                  onChange={(e) => setNeedForm({ ...needForm, quantity: e.target.value })}
                  placeholder="Ej: 50 unidades"
                  className="mt-1"
                  style={inputStyle}
                  data-testid="need-quantity-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>URGENCIA</Label>
                <Select value={needForm.urgency} onValueChange={(v) => setNeedForm({ ...needForm, urgency: v })}>
                  <SelectTrigger className="mt-1" style={selectStyle} data-testid="need-urgency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ borderRadius: 4,  }}>
                    {URGENCY_LEVELS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2" style={{ backgroundColor: u.color }} />
                          {u.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <button
              onClick={handleCreateNeed}
              disabled={loading}
              className="w-full py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5 disabled:opacity-50"
              style={{ fontFamily: B.font, background: C.blue, color: "#fff", borderRadius: 4 }}
              data-testid="submit-need-btn"
            >
              {loading ? "PUBLICANDO..." : "PUBLICAR NECESIDAD"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Offers Dialog */}
      <Dialog open={!!showOffers} onOpenChange={() => setShowOffers(null)}>
        <DialogContent className="sm:max-w-lg" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: C.lilac, borderRadius: 4 }}>
                <Users className="w-4 h-4 text-white" />
              </div>
              OFERTAS DE AYUDA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {(offers[showOffers] || []).length > 0 ? (
              offers[showOffers].map((offer) => (
                <div key={offer.offer_id} className="p-4" style={{ background: "#f8f7f4", borderRadius: 4 }} data-testid={`offer-review-${offer.offer_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-bold" style={{ color: "#2D3561" }}>{offer.volunteer_name}</p>
                      <p className="text-[10px] tracking-widest" style={{ fontFamily: B.mono, color: C.lilac }}>{offer.volunteer_email}</p>
                      {offer.message && (
                        <p className="text-sm mt-2 italic" style={{ color: "#666" }}>"{offer.message}"</p>
                      )}
                      <p className="text-[10px] mt-2 flex items-center gap-1" style={{ fontFamily: B.mono, color: "#ccc" }}>
                        <Clock className="w-3 h-3" /> {new Date(offer.created_at).toLocaleString("es-ES")}
                      </p>
                    </div>
                    {offer.status === "pendiente" && (
                      <div className="flex gap-2 ml-3">
                        <BBtn onClick={() => handleOfferStatus(offer.offer_id, "aceptada")} variant="ghost" testId={`accept-offer-${offer.offer_id}`} style={{ border: "1px solid #8B9A2D", color: "#8B9A2D", borderRadius: 4 }}>
                          <CheckCircle className="w-3 h-3" /> ACEPTAR
                        </BBtn>
                        <BBtn onClick={() => handleOfferStatus(offer.offer_id, "rechazada")} variant="ghost" testId={`reject-offer-${offer.offer_id}`}>
                          <XCircle className="w-3 h-3" /> RECHAZAR
                        </BBtn>
                      </div>
                    )}
                    {offer.status === "aceptada" && (
                      <div className="flex items-center gap-2 ml-3">
                        <BBadge bg="#8B9A2D">ACEPTADA</BBadge>
                        <BBtn
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/conversations`, { other_user_id: offer.volunteer_id, need_id: showOffers }, { headers, withCredentials: true });
                              setShowOffers(null);
                              setActiveTab("chat");
                              toast.success("Conversacion creada - ve al tab Chat");
                            } catch { toast.error("Error al iniciar chat"); }
                          }}
                          variant="secondary"
                          testId={`chat-offer-${offer.offer_id}`}
                        >
                          <MessageCircle className="w-3 h-3" /> CHAT
                        </BBtn>
                      </div>
                    )}
                    {offer.status !== "pendiente" && offer.status !== "aceptada" && (
                      <BBadge bg="#999">{offer.status}</BBadge>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-sm" style={{ color: "#999" }}>No hay ofertas aún</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="sm:max-w-md" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: C.blue, borderRadius: 4 }}>
                <Building2 className="w-4 h-4 text-white" />
              </div>
              EDITAR PERFIL
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>NOMBRE DE CONTACTO</Label>
              <Input
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                className="mt-1"
                style={inputStyle}
                data-testid="edit-shelter-name"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>NOMBRE DE LA PROTECTORA</Label>
              <Input
                value={profileForm.organization_name}
                onChange={(e) => setProfileForm({ ...profileForm, organization_name: e.target.value })}
                className="mt-1"
                style={inputStyle}
                data-testid="edit-shelter-org-name"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest flex items-center gap-2" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                <MapPin className="w-4 h-4" style={{ color: "#e8637a" }} /> UBICACIÓN
              </Label>
              <MunicipalitySelector
                value={profileForm.location_name}
                onChange={(name) => setProfileForm({ ...profileForm, location_name: name })}
                onLocationSelect={(lat, lng, name) => {
                  setProfileForm((f) => ({ ...f, location_lat: lat, location_lng: lng, location_name: name }));
                  toast.success(`${name} seleccionado`);
                }}
                placeholder="Buscar municipio..."
                className="mt-1"
              />
              {profileForm.location_lat && (
                <p className="text-[10px] mt-1 flex items-center gap-1" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>
                  <MapPin className="w-3 h-3" />
                  {profileForm.location_lat.toFixed(4)}, {profileForm.location_lng.toFixed(4)}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESCRIPCIÓN</Label>
              <Textarea
                value={profileForm.bio}
                onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                placeholder="Cuéntanos sobre tu organización..."
                className="mt-1"
                style={inputStyle}
                data-testid="edit-shelter-bio"
              />
            </div>
            <button
              onClick={handleSaveProfile}
              className="w-full py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5"
              style={{ fontFamily: B.font, background: C.blue, color: "#fff", borderRadius: 4 }}
              data-testid="save-shelter-profile-btn"
            >
              GUARDAR CAMBIOS
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Need Dialog */}
      <Dialog open={!!editingNeed} onOpenChange={(open) => !open && setEditingNeed(null)}>
        <DialogContent className="max-w-md" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: C.lilac, borderRadius: 4 }}>
                <Edit3 className="w-4 h-4 text-white" />
              </div>
              EDITAR NECESIDAD
            </DialogTitle>
            <DialogDescription style={{ fontFamily: B.mono, fontSize: "11px" }}>
              Actualiza los detalles según vayan llegando ayudas
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TÍTULO</Label>
              <Input
                value={editNeedForm.title}
                onChange={(e) => setEditNeedForm({ ...editNeedForm, title: e.target.value })}
                className="mt-1"
                style={inputStyle}
                data-testid="edit-need-title"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESCRIPCIÓN</Label>
              <Textarea
                value={editNeedForm.description}
                onChange={(e) => setEditNeedForm({ ...editNeedForm, description: e.target.value })}
                className="mt-1"
                rows={3}
                style={inputStyle}
                data-testid="edit-need-description"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>CANTIDAD NECESARIA</Label>
              <Input
                value={editNeedForm.quantity}
                onChange={(e) => setEditNeedForm({ ...editNeedForm, quantity: e.target.value })}
                placeholder="Ej: 20 mantas (ya tenemos 30)"
                className="mt-1"
                style={inputStyle}
                data-testid="edit-need-quantity"
              />
              <p className="text-[10px] mt-1" style={{ fontFamily: B.mono, color: "#999" }}>
                Puedes indicar lo que ya tienes y lo que falta
              </p>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>URGENCIA</Label>
              <Select value={editNeedForm.urgency} onValueChange={(v) => setEditNeedForm({ ...editNeedForm, urgency: v })}>
                <SelectTrigger className="mt-1" style={selectStyle} data-testid="edit-need-urgency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ borderRadius: 4,  }}>
                  {URGENCY_LEVELS.map((u) => (
                    <SelectItem key={u.value} value={u.value}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2" style={{ backgroundColor: u.color }} />
                        {u.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEditingNeed(null)}
                className="flex-1 py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5"
                style={{ fontFamily: B.font, background: "#fff", color: "#2D3561",  }}
              >
                CANCELAR
              </button>
              <button
                onClick={handleSaveNeedEdit}
                className="flex-1 py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5"
                style={{ fontFamily: B.font, background: C.lilac, color: "#fff", borderRadius: 4 }}
                data-testid="save-need-edit-btn"
              >
                GUARDAR CAMBIOS
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Emergency Dialog */}
      <Dialog open={!!editingEmergency} onOpenChange={(open) => !open && setEditingEmergency(null)}>
        <DialogContent className="sm:max-w-lg" style={dialogStyle}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: C.red, borderRadius: 4 }}>
                <Edit3 className="w-4 h-4 text-white" />
              </div>
              EDITAR EMERGENCIA
            </DialogTitle>
            <DialogDescription style={{ fontFamily: B.mono, fontSize: "11px" }}>
              Actualiza los detalles de la emergencia
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TIPO DE EMERGENCIA</Label>
              <Select value={editEmergencyForm.type} onValueChange={(v) => setEditEmergencyForm({ ...editEmergencyForm, type: v })}>
                <SelectTrigger className="mt-1" style={selectStyle} data-testid="edit-emergency-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] z-[100]" position="popper" sideOffset={4} style={{ borderRadius: 4,  }}>
                  {EMERGENCY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <t.Icon className="w-4 h-4" style={{ color: C.navy }} strokeWidth={1.8} />
                        <span className="font-medium">{t.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>TÍTULO</Label>
              <Input
                value={editEmergencyForm.title}
                onChange={(e) => setEditEmergencyForm({ ...editEmergencyForm, title: e.target.value })}
                className="mt-1"
                style={inputStyle}
                data-testid="edit-emergency-title"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>DESCRIPCIÓN</Label>
              <Textarea
                value={editEmergencyForm.description}
                onChange={(e) => setEditEmergencyForm({ ...editEmergencyForm, description: e.target.value })}
                className="mt-1"
                style={inputStyle}
                data-testid="edit-emergency-description"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest" style={{ fontFamily: B.mono, color: "#2D3561" }}>UBICACIÓN</Label>
              <Input
                value={editEmergencyForm.location_name}
                onChange={(e) => setEditEmergencyForm({ ...editEmergencyForm, location_name: e.target.value })}
                className="mt-1"
                style={inputStyle}
                data-testid="edit-emergency-location"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEditingEmergency(null)}
                className="flex-1 py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5"
                style={{ fontFamily: B.font, background: "#fff", color: "#2D3561",  }}
              >
                CANCELAR
              </button>
              <button
                onClick={handleSaveEmergencyEdit}
                className="flex-1 py-3 font-bold text-sm uppercase tracking-wider transition-all duration-150 hover:-translate-y-0.5"
                style={{ fontFamily: B.font, background: C.red, color: "#fff", borderRadius: 4 }}
                data-testid="save-emergency-edit-btn"
              >
                GUARDAR CAMBIOS
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
